import React, { Children, useEffect, useRef, useState } from 'react';
import { ShowMoreButton } from '@fabric-ui/components';
import { cellContentStyle } from '@fabric-ui/theme';
import { emptyObject } from '@fabric-ui/utils';
import { Box, Skeleton, SxProps, Theme } from '@mui/material';

export interface AvCollapseContentProps {
  maxLines?: number;
  isListMode?: boolean;
  shouldBreakWords?: boolean;
  /**
   * @deprecated - Use children instead
   */
  content?: React.ReactNode;
  children?: React.ReactNode;
  loading?: boolean;
  contentSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  showArrowIcon?: boolean;
  displayCounter?: boolean;
}

export const AvCollapseContent: React.FC<AvCollapseContentProps> = ({
  maxLines = 1,
  isListMode = false,
  shouldBreakWords = false,
  content,
  children,
  loading = false,
  contentSx = emptyObject,
  containerSx = emptyObject,
  showArrowIcon = false,
  displayCounter = false,
}) => {
  const [showMore, setShowMore] = useState(false);
  const [isOverflowed, setIsOverflow] = useState(false);

  const textElementRef = useRef<HTMLDivElement>(null);

  const finalContent = children ?? content;

  const onPressShowMore = () => setShowMore(prev => !prev);

  useEffect(() => {
    const compareSize = () =>
      textElementRef.current && setIsOverflow(textElementRef.current.scrollHeight > textElementRef.current.clientHeight);
    if (textElementRef.current && finalContent) {
      compareSize();
      window.addEventListener('resize', compareSize);
      return () => window.removeEventListener('resize', compareSize);
    }
    return undefined;
  }, [loading, finalContent]);

  useEffect(() => {
    const onSidePanelResize = () => setShowMore(false);
    window.addEventListener('onSidePanelResize', onSidePanelResize);
    return () => window.removeEventListener('onSidePanelResize', onSidePanelResize);
  }, []);

  if (isListMode) {
    const items = Children.toArray(children);
    const hasMoreItems = items.length > maxLines;
    const itemsToShow = hasMoreItems && !showMore ? items.slice(0, maxLines) : items;

    return (
      <Box sx={containerSx}>
        {itemsToShow}
        {hasMoreItems && (
          <ShowMoreButton
            showMore={showMore}
            onPress={onPressShowMore}
            showArrowIcon={showArrowIcon}
            isListMode={isListMode}
            itemsLength={items.length}
            maxLines={maxLines}
            displayCounter={displayCounter}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <Box
        ref={textElementRef}
        sx={[
          cellContentStyle,
          shouldBreakWords ? { wordBreak: 'break-word' } : {},
          showMore ? { WebkitLineClamp: 'initial' } : { WebkitLineClamp: maxLines },
          { overflowY: 'scroll' },
          contentSx,
        ]}>
        {loading ? <Skeleton variant="text" height={21} width="80%" /> : finalContent}
      </Box>
      {!loading && isOverflowed && <ShowMoreButton showMore={showMore} onPress={onPressShowMore} showArrowIcon={showArrowIcon} />}
    </Box>
  );
};
